import { gql } from '@apollo/client';

const IS_LOGGED_IN = gql`
  query IsUserLoggedIn {
    isLoggedIn @client
  }
`;

const GET_SHIPS = gql`
  query GetShips($from: String, $to: String) {
    ships {
      code
      name
    }
    searchSailings(
      input: { from: $from, to: $to }
      includeSoldOutSailings: true
      onlyActive: false
    ) {
      dateRange {
        from
        to
      }
      shipSailings {
        shipCode
        shipName
        sailingDates
      }
    }
  }
`;

const GET_ENCRYPTED = gql`
  query GetEncrypt($object: JSON) {
    encrypt(object: $object)
  }
`;

const BOOKING_FRAGMENT = gql`
  fragment Booking on Booking {
    id
    token
    bookingDate
    isCasinoBooking
    country {
      office {
        code
      }
      currencies {
        symbol
      }
      languages {
        code
      }
      phoneNumber
      code
    }
    upgradesInfo {
      bookingId
      upgradeEligible
      offerUrl
    }
    guests {
      age
      airAccommodations {
        flightNumber
      }
      birthDate
      cas
      email
      firstName
      gender
      index
      lastName
      nationality
      stateCode
      travelDocument
      phoneNumber
      _mutableFields
    }
    price {
      balanceDue
      balanceDueDate
      currencyCode
      depositAmount
      depositAmountOwed
      depositDueDate
      depositPaid
      gratuities
      originalCruiseFare
      subTotal
      taxesFees
      paymentsApplied
      total
      totalBalanceDue
      travelProtection
      preTour
      flightCosts
      inFinal
      depositAmountOwed
    }
    status
    stateroom {
      accessible
      deck {
        code
        name
      }
      number
      position
      subType
      type
      image
    }
    sailing {
      sailDate
      itinerary {
        preTour {
          duration
        }
        description
        totalNights
        ship {
          code
          name
        }
        code
        name
        destination {
          code
          name
        }
        ship {
          code
          name
        }
        departurePort {
          name
          region
        }
        media {
          images {
            path
          }
        }
        days {
          number
          type
          ports {
            activity
            arrivalTime
            departureTime
            port {
              code
              name
              region
            }
          }
        }
      }
    }
    type
  }
`;

const FLIGHT_PROFILE_FRAGMENT = gql`
  fragment FlightProfile on FlightProfile {
    id
    currency
    guests {
      id
      flightGuest {
        firstName
        lastName
        allowCancellation
        allowReshop
        gdsLocators
        airChargesAmount
        airPenaltyAmount
        airFee
        airFeeAfterDueDate
        paymentDate
        refundableFlag
        transfers {
          componentType
          price
          status
          transferCode
          transferDescription
          type
        }
        cancelationPenalities {
          amount
          isFinalPayment
          isRefundable
        }
        guestFlights {
          arrivalDate
          arrivalTime
          departDate
          departTime
          flightIcon
          routeDirection
          confirmationNumbers
          totalDurationMins
          numberOfStops
          gdsLocator
          flightSegments {
            number
            operatingAirline {
              code
              name
              icon
            }
            marketingAirline {
              code
              name
              icon
            }
            cabinClass {
              name
              code
            }
            durationMins
            departure {
              airport {
                code
              }
            }
            layoverMins
            airplane {
              code
              name
            }
            arrival {
              airport {
                name
                code
                city
                country
              }
              date
              time
            }
            departure {
              airport {
                name
                code
                city
                country
              }
              date
              time
            }
          }
        }
      }
    }
  }
`;

const RETRIEVE_BOOKING = gql`
  ${BOOKING_FRAGMENT}
  ${FLIGHT_PROFILE_FRAGMENT}
  query retrieveBooking(
    $bookingId: String!
    $lastName: String!
    $shipCode: String!
    $sailDate: String!
  ) {
    retrieveBooking(
      input: {
        bookingId: $bookingId
        lastName: $lastName
        shipCode: $shipCode
        sailDate: $sailDate
        application: AMEND
      }
    ) {
      token
      booking {
        ...Booking
        flightProfile {
          ...FlightProfile
        }
      }
    }
  }
`;

const RETRIEVE_BOOKING_TOKEN = gql`
  query retrieveBooking(
    $bookingId: String!
    $lastName: String!
    $shipCode: String!
    $sailDate: String!
  ) {
    retrieveBooking(
      input: {
        bookingId: $bookingId
        lastName: $lastName
        shipCode: $shipCode
        sailDate: $sailDate
        application: AMEND
      }
    ) {
      token
    }
  }
`;

const RETRIEVE_BOOKING_FCC = gql`
  ${BOOKING_FRAGMENT}
  query retrieveBooking(
    $bookingId: String!
    $lastName: String!
    $shipCode: String!
    $sailDate: String!
  ) {
    retrieveBooking(
      input: {
        bookingId: $bookingId
        lastName: $lastName
        shipCode: $shipCode
        sailDate: $sailDate
        application: AMEND
      }
    ) {
      token
      booking {
        ...Booking
      }
    }
  }
`;

const RETRIEVE_GUEST_PROFILE_BOOKING = gql`
  ${BOOKING_FRAGMENT}
  ${FLIGHT_PROFILE_FRAGMENT}
  query retrieveBookingByGAProfile($accessToken: String!, $bookingId: String, $token: String) {
    retrieveBookingByGAProfile(accessToken: $accessToken, bookingId: $bookingId, token: $token) {
      token
      booking {
        id
        upgradesInfo {
          bookingId
          upgradeEligible
          offerUrl
        }
        ...Booking
        flightProfile {
          ...FlightProfile
        }
      }
      shortProfileBooking {
        bookingId
        lastName
        sailDate
        shipCode
        token
        endDate
        shipName
        type
      }
    }
  }
`;

const CURRENT_BOOKING = gql`
  ${BOOKING_FRAGMENT}
  ${FLIGHT_PROFILE_FRAGMENT}
  query currentBooking {
    currentBooking {
      ...Booking
      paymentHistory {
        amount
        date
        type
        status
        postedDate
      }
      flightProfile {
        ...FlightProfile
      }
    }
  }
`;

const CURRENT_BOOKING_FCC = gql`
  ${BOOKING_FRAGMENT}
  query currentBooking {
    currentBooking {
      ...Booking
    }
  }
`;

const CURRENT_BOOKING_GRP = gql`
  ${BOOKING_FRAGMENT}
  ${FLIGHT_PROFILE_FRAGMENT}
  query currentBooking {
    currentBooking {
      ...Booking
      flightProfile {
        ...FlightProfile
      }
    }
  }
`;

const GET_COUNTRIES = gql`
  query GetCountries {
    allCountries {
      code
      isoAlpha2Code
      name
      divisions {
        code
        name
      }
    }
  }
`;

const GET_PAYMENT_HISTORY = gql`
  query currentBooking {
    currentBooking {
      paymentHistory {
        amount
        date
        type
        status
        postedDate
      }
    }
  }
`;

const LAUNCH_DARKLY_FLAGS = gql`
  query ldFlags(
    $ldUser: String!
    $application: Application
    $officeId: String
    $language: String
    $brand: Brand
    $country: String
    $clientApp: ClientApplication
    $currency: String
    $bookingCountry: String
  ) {
    ldFlags(
      ldUser: $ldUser
      application: $application
      officeID: $officeId
      language: $language
      brand: $brand
      country: $country
      clientApp: $clientApp
      currency: $currency
      bookingCountry: $bookingCountry
    )
  }
`;

const GIFT_CARD_BALANCE_INQUIRY = gql`
  query giftCardBalanceInquiry($accountNumber: String!, $cvv2: String!, $expirationDate: String!) {
    giftCardBalanceInquiry(
      input: { accountNumber: $accountNumber, cvv2: $cvv2, expirationDate: $expirationDate }
    ) {
      accountNumber
      price {
        value
        currency
      }
      balanceResponse
      status
    }
  }
`;

const LITE_PROFILE = gql`
  query LiteProfile {
    getLiteProfile {
      loyaltyData
      creationDate
      loyaltyPoints
      loyaltyTier
    }
  }
`;

export {
  RETRIEVE_GUEST_PROFILE_BOOKING,
  BOOKING_FRAGMENT,
  GET_ENCRYPTED,
  FLIGHT_PROFILE_FRAGMENT,
  IS_LOGGED_IN,
  GET_SHIPS,
  CURRENT_BOOKING,
  CURRENT_BOOKING_FCC,
  CURRENT_BOOKING_GRP,
  GET_COUNTRIES,
  GET_PAYMENT_HISTORY,
  LAUNCH_DARKLY_FLAGS,
  GIFT_CARD_BALANCE_INQUIRY,
  RETRIEVE_BOOKING,
  RETRIEVE_BOOKING_FCC,
  RETRIEVE_BOOKING_TOKEN,
  LITE_PROFILE,
};
